/* The frame every unauthenticated screen sits in: brand, one card, an optional
 * line underneath.
 *
 * The layout is `styles/auth.css`, promoted from the prototype where it was
 * re-authored per screen. One home for it is the point — `design.md` warns that
 * a shell repeated per flow is one decision in several copies, and they drift.
 *
 * It also owns the focus move. On navigation the heading takes focus, so a
 * keyboard or screen-reader user is told the page changed rather than being
 * left at the top of something that silently replaced itself. Doing it here
 * rather than per screen is what stops one screen forgetting.
 */

import type { ReactNode } from 'react';
import { useEffect, useRef } from 'react';
import { LanguagePicker } from './language-picker.js';

export interface AuthScreenProps {
  readonly title: string;
  readonly brand: string;
  readonly subtitle?: string;
  readonly children: ReactNode;
  /** The "Already have an account?" line. Rendered outside the card. */
  readonly footer?: ReactNode;
  /**
   * Whether to offer the language control below the card — STORY-042.
   *
   * On by default, because the screens that use this frame are the ones with
   * no nav bar to put it in. The Account screen turns it off: it sits in this
   * frame too, and it has a real home for the control with a heading and a
   * sentence saying whose setting it is. Two pickers on one screen is two
   * places to answer one question.
   */
  readonly offersLanguage?: boolean;
  /**
   * A way back, rendered above the brand.
   *
   * Absent on sign-in and sign-up, where there is nowhere to go back TO. The
   * Account screen supplies one because it sits outside the workspace shell and
   * so has no navigation at all — the only way out was the browser's back
   * button.
   */
  readonly back?: ReactNode;
  /**
   * A wider card, for a screen that is a settings page rather than a form.
   *
   * The default 26rem is right for sign-in: a short column of fields reads
   * badly wider. The Account screen carries sections, a language control and a
   * destructive action, and at 26rem on a desktop it reads as a phone layout
   * somebody forgot to finish.
   */
  readonly wide?: boolean;
}

export function AuthScreen({
  title,
  brand,
  subtitle,
  children,
  footer,
  offersLanguage = true,
  back,
  wide = false,
}: AuthScreenProps) {
  const heading = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    /* Keyed on the title, so moving between auth screens moves focus again
     * rather than only on first mount. */
    heading.current?.focus();
  }, []);

  return (
    <div className={wide ? 'auth auth--wide' : 'auth'}>
      {back ? <div className="auth__back">{back}</div> : null}
      <p className="auth__brand">{brand}</p>
      <div className="card card--auth">
        <div className="card__header">
          {/* `tabindex="-1"` makes a non-interactive heading focusable without
              putting it in the tab order. `auth.css` removes the ring, because
              a focus ring on a heading nobody clicked reads as a rendering
              fault rather than as a cue. */}
          <h1 className="card__title" tabIndex={-1} ref={heading}>
            {title}
          </h1>
        </div>
        <div className="card__body">
          {subtitle ? <p className="card__subtitle">{subtitle}</p> : null}
          {children}
        </div>
      </div>
      {footer ? <p className="auth__alt">{footer}</p> : null}

      {/* Every unauthenticated screen goes through here, which is what makes
          this the one place that reaches all of them — STORY-042 asks for a
          home that is not a nav bar, because at this point there is not one.
          Below the card rather than above it: it is a way out for somebody who
          cannot read the page, not the first thing anybody needs. */}
      {offersLanguage ? (
        <div className="auth__language">
          <LanguagePicker />
        </div>
      ) : null}
    </div>
  );
}
